import { MAIN_URL, getApiHeaders } from "../Configurations/Urls";
import { buildDatagridKey } from "../Configurations/datagridKey";
import {
  buildBackendListColumns,
  mergeColumnLayouts,
  titleCaseFieldKey,
} from "./buildBackendListColumns";

export function resolveOrganizationId(userData) {
  return (
    userData?.organization?.organization_id ??
    userData?.organization_id ??
    userData?.user_active_organization?.organization_id ??
    null
  );
}

export function resolveOrganizationUserId(userData) {
  return (
    userData?.organization_user_id ??
    userData?.organization_user?.organization_user_id ??
    userData?.user_active_organization?.organization_user_id ??
    null
  );
}

/** Extract row array from common list API response shapes. */
export function extractApiListRows(payload) {
  if (payload == null || typeof payload !== "object") return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  for (const value of Object.values(payload)) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object" && Array.isArray(value.data)) {
      return value.data;
    }
  }
  return [];
}

/** Normalize general/org datagrid columns into PersonalizeDataGrid / list shape. */
export function normalizeCatalogColumns(columns = [], columnLabels = {}) {
  const list = Array.isArray(columns) ? columns : [];
  return list
    .map((col, index) => {
      const key = col?.field ?? col?.key;
      if (key == null || key === "") return null;
      const fieldKey = String(key);
      return {
        ...col,
        key: fieldKey,
        field: fieldKey,
        label:
          columnLabels[fieldKey] ||
          col.label ||
          titleCaseFieldKey(fieldKey),
        width: Number(col.width) || 150,
        visible: !!col.visible,
        filterable: col.filterable !== false,
        sortable: col.sortable !== false,
        pinned:
          col.pinned === true || col.pinned === "left"
            ? "left"
            : col.pinned || undefined,
        order: col.order ?? index,
        required: !!(col.required || col.mandatory),
      };
    })
    .filter(Boolean);
}

/**
 * Load General Datagrid Configuration columns for a datagrid key.
 * Uses prefer_general get-by-context (single row) instead of listing all grids.
 * @returns {Promise<object[]|null>}
 */
export async function fetchGeneralDatagridColumns(datagridKeyBase, orgId) {
  if (!datagridKeyBase || !orgId) return null;

  const fullKey = buildDatagridKey(datagridKeyBase, orgId);
  if (!fullKey) return null;

  try {
    const contextRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({
        datagrid_key: fullKey,
        prefer_general: true,
      }),
    });
    if (contextRes.ok) {
      const contextData = await contextRes.json();
      const columns =
        contextData?.datagrid?.datagrid_default_configuration?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        return columns;
      }
    }

    // Fallback: list endpoint (legacy / when prefer_general unavailable)
    const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
      method: "GET",
      headers: getApiHeaders(),
    });
    if (!configRes.ok) return null;

    const configResponse = await configRes.json();
    const datagrids = configResponse.datagrids || [];
    const bareKey = String(datagridKeyBase).trim();
    const saved =
      datagrids.find((dg) => dg.datagrid_key === fullKey) ||
      datagrids.find((dg) => dg.datagrid_key === bareKey);

    const columns = saved?.datagrid_default_configuration?.columns;
    return Array.isArray(columns) && columns.length > 0 ? columns : null;
  } catch (error) {
    console.warn("Personalize general datagrid fetch failed:", error);
    return null;
  }
}

function buildLabelMap(columnLabels = {}, softFallbackColumns = null) {
  const labels = { ...columnLabels };
  if (Array.isArray(softFallbackColumns)) {
    softFallbackColumns.forEach((col) => {
      const key = col?.field ?? col?.key;
      if (key != null && col?.label && !labels[key]) {
        labels[String(key)] = col.label;
      }
    });
  }
  return labels;
}

/**
 * Resolve Personalize default columns:
 * 1) General Datagrid Configuration (admin defaults)
 * 2) API sample row + metadata
 * 3) Soft fallback columns (labels-only legacy defaults)
 */
export async function resolvePersonalizeCatalogColumns({
  datagridKeyBase,
  orgId,
  columnLabels = {},
  sampleRow = null,
  metadataFields = [],
  softFallbackColumns = null,
} = {}) {
  const labels = buildLabelMap(columnLabels, softFallbackColumns);

  let catalogColumns = normalizeCatalogColumns(
    await fetchGeneralDatagridColumns(datagridKeyBase, orgId),
    labels
  );

  if (catalogColumns.length === 0) {
    catalogColumns = buildBackendListColumns({
      metadataFields,
      sampleRow,
    }).map((col) => ({
      ...col,
      label: labels[col.key || col.field] || col.label,
    }));
  }

  if (catalogColumns.length === 0 && Array.isArray(softFallbackColumns)) {
    catalogColumns = normalizeCatalogColumns(softFallbackColumns, labels);
  }

  return catalogColumns;
}

/**
 * Load list columns the same way Personalize View does:
 * general admin catalog ∪ get-by-context prefs (via mergeColumnLayouts).
 */
export async function loadMergedDatagridColumns({
  datagridKeyBase,
  orgId,
  organizationUserId = null,
  columnLabels = {},
  softFallbackColumns = null,
  mapSavedColumns = null,
} = {}) {
  const labels = buildLabelMap(columnLabels, softFallbackColumns);
  const datagridKey = buildDatagridKey(datagridKeyBase, orgId);

  const requestPayload =
    datagridKey && orgId
      ? {
          datagrid_key: datagridKey,
          organization_id: orgId,
          ...(organizationUserId
            ? { organization_user_id: organizationUserId }
            : {}),
        }
      : null;

  // Fetch catalog + prefs in parallel (avoids serial round-trips).
  const [generalColumns, configRes] = await Promise.all([
    fetchGeneralDatagridColumns(datagridKeyBase, orgId),
    requestPayload
      ? fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: getApiHeaders(),
          body: JSON.stringify(requestPayload),
        }).catch((error) => {
          console.warn(
            "loadMergedDatagridColumns get-by-context failed:",
            error
          );
          return null;
        })
      : Promise.resolve(null),
  ]);

  let catalogColumns = normalizeCatalogColumns(generalColumns, labels);
  if (catalogColumns.length === 0 && Array.isArray(softFallbackColumns)) {
    catalogColumns = normalizeCatalogColumns(softFallbackColumns, labels);
  }

  if (!datagridKey || !orgId) {
    return { tableConfig: null, columns: catalogColumns, type: null };
  }

  try {
    if (configRes?.ok) {
      const configResponse = await configRes.json();
      const serverCfg =
        configResponse?.datagrid?.datagrid_default_configuration;
      const responseType = configResponse?.type || null;

      if (
        serverCfg &&
        Array.isArray(serverCfg.columns) &&
        serverCfg.columns.length > 0
      ) {
        // When prefs are already general and catalog empty, use prefs as catalog.
        const catalogForMerge =
          catalogColumns.length > 0
            ? catalogColumns
            : normalizeCatalogColumns(serverCfg.columns, labels);

        let labeledColumns = mergeColumnLayouts(
          catalogForMerge,
          serverCfg.columns.map((col) => {
            const fieldKey = col.field || col.key;
            return {
              ...col,
              label: labels[fieldKey] || col.label,
            };
          })
        );

        if (typeof mapSavedColumns === "function") {
          labeledColumns = mapSavedColumns(labeledColumns);
        }

        return {
          tableConfig: { ...serverCfg, columns: labeledColumns },
          columns: labeledColumns,
          type: responseType,
        };
      }
    }
  } catch (error) {
    console.warn("loadMergedDatagridColumns merge failed:", error);
  }

  let columns = catalogColumns;
  if (typeof mapSavedColumns === "function" && columns.length > 0) {
    columns = mapSavedColumns(columns);
  }

  return {
    tableConfig: columns.length
      ? { columns, filters: [], timestamp: new Date().toISOString() }
      : null,
    columns,
    type: columns.length ? "general" : null,
  };
}
